/* eslint-disable complexity */
import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { Option, Select } from 'carbon-react/lib/components/select';

import { formatCurrency } from '@futrli/localisation';

import * as styles from './invoices-summary.scss';
import { Data } from '../../../../utils/table-filters/filter-table-types';
import { INVOICE_DETAIL_KIND } from '../../../../core/constants';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import dueDateArrow from './due-date-arrow.png';
import netCashFlowArrow from './net-cashflow-arrow.png';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../forecast/onboarding/utils/hooks';
import { SCENARIO_WITH_INVOICES_ONBOARDING_STEP } from '../../../../forecast/onboarding/const';
import { translatedInvoicesSummaryOptions } from './types';

interface Props {
  onDueDateSummaryChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dueDateValue: string;
  data?: Data[];
}

export const InvoicesSummary = ({ onDueDateSummaryChange, dueDateValue, data }: Props) => {
  const { onboardingStep, editInvoicesBills } = useOnboardingUrlInfo();

  const intl = useIntl();
  const { currency } = useContext(PredictManagementContext);

  const showAnimatedArrows = useAnimationLatency(
    data && onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED && !editInvoicesBills
  );

  const invoicesSummaryOptions = useMemo(() => translatedInvoicesSummaryOptions(intl), [intl]);

  const invoicesSum = useMemo(() => {
    if (data) {
      const invoices = data.filter(d => d.type === INVOICE_DETAIL_KIND.INVOICE && !d.excluded_from_report);
      const invoicesSum = invoices.reduce((acc, currValue) => {
        if (currValue.converted_amount_due) {
          return acc + currValue.converted_amount_due;
        }
        return acc;
      }, 0);
      return invoicesSum;
    }
    return 0;
  }, [data]);

  const billsSum = useMemo(() => {
    if (data) {
      const bills = data.filter(d => d.type === INVOICE_DETAIL_KIND.BILL && !d.excluded_from_report);
      const billsSum = bills.reduce((acc, currValue) => {
        if (currValue.converted_amount_due) {
          return acc + currValue.converted_amount_due;
        }
        return acc;
      }, 0);
      return billsSum;
    }
    return 0;
  }, [data]);

  const netCashFlow = invoicesSum - billsSum;

  if (!data) {
    return null;
  }

  return (
    <div className={styles.container}>
      {onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED && !editInvoicesBills && (
        <img
          alt="onboarding-arrow"
          src={dueDateArrow}
          style={{ position: 'absolute', zIndex: 12, top: 72, left: 37 }}
          className={classNames(styles.fadeInContainer, {
            [styles.fadeIn]: showAnimatedArrows
          })}
        />
      )}
      <div className={styles.selectContainer}>
        <Select
          onChange={onDueDateSummaryChange}
          value={dueDateValue}
          autoComplete="off"
          size="medium"
          label={intl.formatMessage({
            id: 'invoices.cash_increase_decrease',
            defaultMessage: 'Cash increase/decrease from due invoices and bills'
          })}
          listMaxHeight={400}
        >
          {invoicesSummaryOptions.map(o => (
            <Option key={o.value} text={o.text} value={o.value} />
          ))}
        </Select>
      </div>
      <div className={styles.valueContainer}>
        <div className={styles.value}>
          {'+'}
          {formatCurrency(invoicesSum, intl, currency || undefined, 0, 0)}
        </div>
        <div className={styles.caption}>
          <FormattedMessage
            id="onboarding.invoices_increasing"
            defaultMessage="Invoices <b>increasing</b> cash flow"
            values={{
              b: (chunks: string) => <span style={{ fontWeight: 500 }}>{chunks}</span>
            }}
          />
        </div>
      </div>
      <div className={styles.operatorContainer}>
        <div className={styles.value} style={{ marginRight: 0 }}>
          {'+'}
        </div>
      </div>
      <div className={styles.valueContainer}>
        <div className={styles.value}>
          {'-'}
          {formatCurrency(billsSum, intl, currency || undefined, 0, 0)}
        </div>
        <div className={styles.caption}>
          <FormattedMessage
            id="onboarding.bills_decreasing"
            defaultMessage="Bills <b>decreasing</b> cash flow"
            values={{
              b: (chunks: string) => <span style={{ fontWeight: 500 }}>{chunks}</span>
            }}
          />
        </div>
      </div>
      <div className={styles.operatorContainer}>
        <div className={styles.value} style={{ marginRight: 0 }}>
          {'='}
        </div>
      </div>
      <div className={styles.valueContainer} style={{ position: 'relative' }}>
        {onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED && !editInvoicesBills && (
          <img
            alt="onboarding-arrow"
            src={netCashFlowArrow}
            style={{ position: 'absolute', zIndex: 12, top: -200, left: -100 }}
            className={classNames(styles.fadeInContainer, {
              [styles.fadeIn]: showAnimatedArrows
            })}
          />
        )}

        <div
          className={classNames(styles.value, {
            [styles.decrease]: netCashFlow < 0,
            [styles.increase]: netCashFlow > 0
          })}
        >
          {formatCurrency(netCashFlow, intl, currency || undefined, 0, 0)}
        </div>
        <div className={styles.caption}>
          {netCashFlow > 0 ? (
            <FormattedMessage id="onboarding.increase_net_cash_flow" defaultMessage="Increase in net cash flow" />
          ) : (
            <FormattedMessage id="onboarding.decrease_net_cash_flow" defaultMessage="Decrease in net cash flow" />
          )}
        </div>
      </div>
    </div>
  );
};
